import { forwardRef, InputHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  variant?: 'default' | 'velux';
  boxClassName?: string;
  iconClassName?: string;
}

const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M3.66675 8.29486C4.8399 9.09787 6.91643 11.4693 7.56888 13C8.5601 9.59975 11.1322 4.7064 12.4497 3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="square"
    />
  </svg>
);

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, id, boxClassName, iconClassName, ...props }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    if (label) {
      return (
        <label className="inline-flex items-start gap-2 cursor-pointer">
          <div className="relative inline-flex shrink-0">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              className={cn('sr-only peer', className)}
              {...props}
            />
            <div
              className={cn(
                'w-4 h-4 border border-solid border-velux-gray-300 rounded',
                'transition-colors',
                'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-velux-blue peer-focus-visible:ring-offset-2',
                boxClassName
              )}
            />
            <CheckIcon
              className={cn(
                'absolute inset-0 w-4 h-4 text-velux-black opacity-0 transition-opacity pointer-events-none',
                'peer-checked:opacity-100',
                iconClassName
              )}
            />
          </div>
          <span className="text-sm text-velux-black select-none leading-4">{label}</span>
        </label>
      );
    }

    return (
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          className={cn('sr-only peer', className)}
          {...props}
        />
        <div
          className={cn(
            'w-4 h-4 border border-solid border-velux-gray-300 rounded',
            'transition-colors',
            'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-velux-blue peer-focus-visible:ring-offset-2',
            boxClassName
          )}
        />
        <CheckIcon
          className={cn(
            'absolute left-0 top-0 w-4 h-4 text-velux-black opacity-0 transition-opacity pointer-events-none',
            'peer-checked:opacity-100',
            iconClassName
          )}
        />
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
